"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.GovPowerManager = void 0;
const bignumber_1 = require("@ethersproject/bignumber");
const bytes_1 = require("@ethersproject/bytes");
const handlers_1 = require("./handlers");
class GovPowerManager {
    constructor(_config) {
        this._config = _config;
        this._defaults = [handlers_1.BalanceOfHandler, handlers_1.BalanceOfERC20Handler, handlers_1.BalanceOfERC1155Handler, handlers_1.CheckpointableERC721Handler, handlers_1.VanillaHandler, handlers_1.AllowlistHandler];
        this._all = [...this._defaults, ...(this._config.customStrategies || [])].map(Handler => new Handler(this._config));
    }
    /**
     * Returns a `GovPowerManager` instance for the provided chain configuration
     * @param config The prop house governance power config
     */
    static for(config) {
        return new GovPowerManager(config);
    }
    /**
     * Get a governance power utility class instance
     * @param typeOrAddress The governance power strategy type or address
     */
    get(typeOrAddress) {
        const strategy = this._all.find(s => {
            const address = bignumber_1.BigNumber.from(s.address);
            const hexAddress = address.toHexString();
            const searchVariations = [
                s.type,
                hexAddress,
                (0, bytes_1.hexStripZeros)(hexAddress),
                address.toString(),
            ].map(s => s.toLowerCase());
            return searchVariations.includes(typeOrAddress.toLowerCase());
        });
        if (!strategy) {
            throw new Error(`Unknown governance power strategy type or address: ${typeOrAddress}`);
        }
        return strategy;
    }
    /**
     * @notice Get the address and parameter information for the provided governance power strategy
     * @param strategy The strategy information
     */
    async getStrategyAddressAndParams(strategy) {
        const util = this.get(strategy.strategyType);
        return {
            address: util?.address,
            params: await util.getStrategyParams(strategy),
        };
    }
    async getUserParamsForStrategies(account, timestamp, strategies) {
        return Promise.all(strategies.map(async (strategy) => this.get(strategy.address).getUserParams(account, timestamp, strategy.id)));
    }
    async getPreCallsForStrategies(account, timestamp, strategies) {
        const preCalls = await Promise.all(strategies.map(async (strategy) => this.get(strategy.address).getStrategyPreCalls?.(account, timestamp, strategy.id)));
        return preCalls.flat().filter(call => call !== undefined);
    }
    async getPowerForStrategies(user, timestamp, strategies, filterZeroGovPower = true) {
        const results = await Promise.all(strategies.map(async (strategy) => {
            const handler = this.get(strategy.address);
            return {
                strategy: {
                    ...strategy,
                    address: handler.address,
                },
                govPower: await handler.getPower({
                    ...strategy,
                    address: handler.address,
                    user,
                    timestamp,
                }),
            };
        }));
        if (filterZeroGovPower) {
            return results.filter(({ govPower }) => !govPower.eq(0));
        }
        return results;
    }
    async getTotalPower(user, timestamp, strategies) {
        const results = await this.getPowerForStrategies(user, timestamp, strategies);
        return results.reduce((acc, curr) => acc.add(curr.govPower), bignumber_1.BigNumber.from(0));
    }
}
exports.GovPowerManager = GovPowerManager;
